import React from 'react';
import style from './users.module.scss';
import { Button, Col, DatePicker, Row } from 'antd';
import Search from 'antd/es/transfer/search';
import dayjs from 'dayjs';
import FilterDropdown from '../../../components/filters/FilterDropdown';
import TableSearch from '../../../components/filters/TableSearch';
import GenericTable from '../../../components/tables/GenericTable';
import TableLoadingArea from '../../../components/tables/TableLoadingArea';
import Pagination from '../../../layout/Pagination';
import PropTypes from 'prop-types';
import fixedBottomDatePickerProps from '../../../shared/utils/datePickerPlacement';

const renderSearchField = (searchConfig, key, showFilter) => {
  if (!searchConfig) return null;

  return (
    <Col
      key={key}
      className={`${style.users__header__search} ${
        searchConfig.type === 'dateRange' || searchConfig.wide
          ? 'table-filters__control--wide'
          : ''
      } ${searchConfig.className || ''}`
        .replace(/\s+/g, ' ')
        .trim()}
    >
      {searchConfig.type === 'custom' ? (
        typeof searchConfig.render === 'function' ? (
          searchConfig.render()
        ) : null
      ) : searchConfig.type === 'dateRange' ? (
        <DatePicker.RangePicker
          {...fixedBottomDatePickerProps}
          placeholder={
            Array.isArray(searchConfig.placeholder)
              ? searchConfig.placeholder
              : ['Datum od', 'Datum do']
          }
          className="search-input"
          allowClear
          allowEmpty={[true, true]}
          format="DD.MM.YYYY"
          value={
            Array.isArray(searchConfig.value)
              ? [
                  searchConfig.value[0] ? dayjs(searchConfig.value[0]) : null,
                  searchConfig.value[1] ? dayjs(searchConfig.value[1]) : null,
                ]
              : [null, null]
          }
          onChange={(dates) =>
            searchConfig.onChange(
              dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
              dates?.[1] ? dates[1].format('YYYY-MM-DD') : ''
            )
          }
        />
      ) : searchConfig.type === 'date' ? (
        <div>
          <DatePicker
            {...fixedBottomDatePickerProps}
            placeholder={
              Array.isArray(searchConfig.placeholder)
                ? searchConfig.placeholder[0] || 'Datum'
                : searchConfig.placeholder || 'Datum'
            }
            className="search-input"
            allowClear
            format="DD.MM.YYYY"
            value={searchConfig.value ? dayjs(searchConfig.value) : null}
            onChange={(date) =>
              searchConfig.onChange(date ? date.format('YYYY-MM-DD') : '')
            }
          />
        </div>
      ) : (
        <Search
          value={searchConfig.value || ''}
          placeholder={searchConfig.placeholder || 'Pretraži'}
          className="search-input"
          allowClear
          enterButton
          onChange={(e) => searchConfig.onChange(e.target.value)}
          width={showFilter ? 'auto' : '100%'}
          style={{ minWidth: 200 }}
        />
      )}
    </Col>
  );
};

const TableSection = ({
  showFilter = false,
  createButtonDisplay,
  showCreateButton = true,
  onCreateClick,
  headerActions,
  inlineHeaderActions,
  rightHeaderActions,
  customHeaderContent = null,
  onSearch,
  secondarySearch,
  tertiarySearch,
  quaternarySearch,
  quinarySearch,
  filter,
  onFilterChange,
  columns,
  actions,
  data,
  emptyState,
  showPagination = false,
  currentPage,
  totalPages,
  totalResults,
  onPageChange,
  searchPlaceholder = 'Pretraži',
  page,
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange,
  bulkActionContent = null,
  tableMinWidth,
  tableWrapperClassName,
  customTable = null,
  rowClassName,
  lboSearch,
  bzkSearch,
  phoneSearch,
  renewDateSearch,
  birthdayDateSearch,
  searchFieldOrder,
  paginationLoading,
  tableSearch,
}) => {
  const searchFieldMap = {
    // Jedinstvena pretraga (useTableSearch + src/config/searchConfig.js).
    tableSearch: tableSearch?.types?.length ? (
      <Col key="table-search" className="table-filters__search">
        <TableSearch
          types={tableSearch.types}
          entries={tableSearch.entries}
          onChange={tableSearch.onChange}
        />
      </Col>
    ) : null,
    secondarySearch: renderSearchField(
      secondarySearch,
      'secondary-search',
      showFilter
    ),
    birthdayDateSearch: renderSearchField(
      birthdayDateSearch,
      'birthday-date-search',
      showFilter
    ),
    lboSearch: renderSearchField(lboSearch, 'lbo-search', showFilter),
    bzkSearch: renderSearchField(bzkSearch, 'bzk-search', showFilter),
    phoneSearch: renderSearchField(phoneSearch, 'phone-search', showFilter),
    filter:
      showFilter &&
      filter.sections.map((filterObj, index) => {
        const firstSection = filterObj[0];
        const baseLabel =
          firstSection?.buttonLabel ||
          firstSection?.label ||
          firstSection?.type ||
          'Filter';
        let selectedLabel = baseLabel;
        const filterSelected = filter.selected || {};
        const selectedKey = filterSelected[firstSection?.type || ''];
        const allItems = filterObj.flatMap((section) => section.items || []);

        if (Array.isArray(selectedKey)) {
          if (selectedKey.length === 1) {
            const selectedItem = allItems.find(
              (item) => item.key === selectedKey[0]
            );
            if (typeof selectedItem?.label === 'string') {
              selectedLabel = selectedItem.label;
            }
          } else if (selectedKey.length > 1) {
            selectedLabel = `${baseLabel} (${selectedKey.length})`;
          }
        } else if (selectedKey) {
          const selectedItem = allItems.find(
            (item) => item.key === selectedKey
          );

          if (typeof selectedItem?.label === 'string') {
            selectedLabel = selectedItem.label;
          }
        }

        const layoutClassName =
          firstSection?.layoutClassName || filter.layoutClassName || '';
        // Per-sekciju popupClassName ima prednost nad zajednickim (filter.*),
        // pa jedan dropdown moze da dobije svoj izgled (npr. visinu) bez uticaja
        // na ostale u istom filteru.
        const popupClassName =
          firstSection?.popupClassName || filter.popupClassName;

        return (
          <Col
            key={index}
            className={`table-filters__dropdown ${layoutClassName}`}
          >
            <FilterDropdown
              sections={filterObj}
              selectedFilter={filter.selected}
              onFilterChange={onFilterChange}
              buttonLabel={selectedLabel}
              popupClassName={popupClassName}
            />
          </Col>
        );
      }),
    tertiarySearch: renderSearchField(
      tertiarySearch,
      'tertiary-search',
      showFilter
    ),
    quaternarySearch: renderSearchField(
      quaternarySearch,
      'quaternary-search',
      showFilter
    ),
    renewDateSearch: renderSearchField(
      renewDateSearch,
      'renew-date-search',
      showFilter
    ),
    quinarySearch: renderSearchField(
      quinarySearch,
      'quinary-search',
      showFilter
    ),
  };

  const defaultSearchFieldOrder = [
    'tableSearch',
    'secondarySearch',
    'lboSearch',
    'bzkSearch',
    'phoneSearch',
    'tertiarySearch',
    'quaternarySearch',
    'renewDateSearch',
    'birthdayDateSearch',
    'quinarySearch',
    'filter',
  ];

  const orderedSearchFields = (
    Array.isArray(searchFieldOrder) && searchFieldOrder.length
      ? searchFieldOrder
      : defaultSearchFieldOrder
  ).flatMap((fieldKey) => searchFieldMap[fieldKey] || []);

  const showBulkActions =
    selectable && selectedRowKeys.length > 0 && !!bulkActionContent;
  const hasRightAlignedHeaderAction = Boolean(
    rightHeaderActions || headerActions || (showCreateButton && createButtonDisplay)
  );

  const table = customTable || (
    <GenericTable
      data={data}
      columns={columns}
      actions={actions}
      rowClassName={rowClassName}
      emptyState={emptyState}
      selectable={selectable}
      selectedRowKeys={selectedRowKeys}
      onSelectionChange={onSelectionChange}
      tableMinWidth={tableMinWidth}
      wrapperClassName={tableWrapperClassName}
    />
  );

  return (
    <div>
      {customHeaderContent ? (
        customHeaderContent
      ) : (
        <Row className={style.users__header}>
          <Row className={`${style.users__header__headerRow} table-filters`}>
            {/* <Col className={style.users__header__search}>
              <Search
                placeholder={searchPlaceholder}
                className="search-input"
                allowClear
                enterButton
                onChange={(e) => onSearch(e.target.value)}
                width={showFilter ? 'auto' : '100%'}
              />
            </Col> */}
            {orderedSearchFields}
            {inlineHeaderActions ? <Col>{inlineHeaderActions}</Col> : null}
            {rightHeaderActions ? (
              <Col
                className="table-filters__action"
                style={{ marginLeft: 'auto' }}
              >
                {rightHeaderActions}
              </Col>
            ) : null}
            {/* Akciono dugme (npr. "Dodaj lek") ide u istu traku, desno poravnato
                preko marginLeft:auto - isto kao rightHeaderActions. Ranije je bilo
                u zasebnom redu koji je zbog width:100% na .table-filters padao u
                novi red i zavrsavao levo. */}
            {(headerActions || (showCreateButton && createButtonDisplay)) && (
              <Col
                className={`${style.users__header__headerRow} table-filters__action`}
                style={rightHeaderActions ? undefined : { marginLeft: 'auto' }}
              >
                {headerActions}
                {showCreateButton && createButtonDisplay && (
                  <Button
                    className={style.users__header__addButton}
                    type="primary"
                    onClick={onCreateClick}
                  >
                    {createButtonDisplay}
                  </Button>
                )}
              </Col>
            )}
            {/* Bulk akcije za selektovane redove: poslednje u traci filtera,
                uz desnu ivicu tabele. Ako nema mesta, traka se prelama i one
                prelaze u sledeci red - i dalje desno. */}
            {showBulkActions && (
              <Col
                className={`${style.users__header__headerRow} table-filters__action`}
                style={
                  hasRightAlignedHeaderAction ? undefined : { marginLeft: 'auto' }
                }
              >
                {bulkActionContent}
              </Col>
            )}
          </Row>
        </Row>
      )}

      {/* Stranice sa sopstvenim zaglavljem nemaju traku filtera. */}
      {customHeaderContent && showBulkActions && (
        <Row
          justify="end"
          className={style.users__header__headerRow}
          style={{ marginTop: 10, marginBottom: 12 }}
        >
          {bulkActionContent}
        </Row>
      )}

      {/* Loader za tabelu vodi isto stanje kao paginacija (paginationLoading);
          stranica bez tog stanja nema loader. */}
      {paginationLoading === undefined ? (
        table
      ) : (
        <TableLoadingArea
          loading={paginationLoading}
          isEmpty={Array.isArray(data) ? data.length === 0 : undefined}
        >
          {table}
        </TableLoadingArea>
      )}

      {showPagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          onPageChange={onPageChange}
          loading={paginationLoading}
        />
      )}
    </div>
  );
};

TableSection.propTypes = {
  showFilter: PropTypes.bool,
  createButtonDisplay: PropTypes.string,
  onCreateClick: PropTypes.func,
  headerActions: PropTypes.node,
  inlineHeaderActions: PropTypes.node,
  rightHeaderActions: PropTypes.node,
  customHeaderContent: PropTypes.node,
  onSearch: PropTypes.func.isRequired,
  secondarySearch: PropTypes.shape({
    type: PropTypes.oneOf(['text', 'date', 'dateRange', 'custom']),
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    onChange: PropTypes.func.isRequired,
  }),
  tertiarySearch: PropTypes.shape({
    type: PropTypes.oneOf(['text', 'date', 'dateRange', 'custom']),
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    onChange: PropTypes.func.isRequired,
  }),
  quaternarySearch: PropTypes.shape({
    type: PropTypes.oneOf(['text', 'date', 'dateRange', 'custom']),
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    onChange: PropTypes.func.isRequired,
  }),
  quinarySearch: PropTypes.shape({
    type: PropTypes.oneOf(['text', 'date', 'dateRange', 'custom']),
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    onChange: PropTypes.func.isRequired,
  }),
  filter: PropTypes.shape({
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.any,
            label: PropTypes.string,
          })
        ),
      })
    ),
    selected: PropTypes.object,
  }),
  onFilterChange: PropTypes.func,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
      customCell: PropTypes.func,
    })
  ),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      icon: PropTypes.node,
      onClick: PropTypes.func,
      danger: PropTypes.bool,
      className: PropTypes.string,
    })
  ),
  data: PropTypes.array,
  emptyState: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    actionButton: PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func,
      type: PropTypes.string,
      style: PropTypes.object,
      className: PropTypes.string,
    }),
  }),
  showPagination: PropTypes.bool,
  showCreateButton: PropTypes.bool,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalResults: PropTypes.number,
  onPageChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  selectable: PropTypes.bool,
  selectedRowKeys: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onSelectionChange: PropTypes.func,
  bulkActionContent: PropTypes.node,
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tableWrapperClassName: PropTypes.string,
  customTable: PropTypes.node,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  searchFieldOrder: PropTypes.arrayOf(PropTypes.string),
  tableSearch: PropTypes.shape({
    types: PropTypes.array,
    entries: PropTypes.array,
    onChange: PropTypes.func,
  }),
  paginationLoading: PropTypes.bool,
};

export default TableSection;
